import type { ColumnDef } from '@tanstack/react-table';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { DataTable, createActionsColumn, createSerialColumn } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { HighlightText } from '@/components/common/HighlightText';
import { RowActionButton } from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import { bookProjectsInfiniteQueryOptions } from '../api/productRequisition.api';
import { useRequisitionFilters } from '../hooks/useRequisitionFilters';
import type { BookProject, StudentProjectSearch } from '../types/productRequisition.types';

const route = getRouteApi('/_auth/product/booklisted-institution/');

function getBookProjectColumns(
  searchText: string | undefined,
  onEnter: (row: BookProject) => void,
): ColumnDef<BookProject, unknown>[] {
  return [
    createSerialColumn<BookProject>(),
    {
      accessorKey: 'name',
      header: 'Project Name',
      cell: ({ row }) => (
        <span className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Project Code',
      cell: ({ row }) => row.original.code ?? EMPTY_VALUE,
    },
    {
      accessorKey: 'isActive',
      header: 'Is Active',
      cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive'),
    },
    {
      accessorKey: 'startDate',
      header: 'Start Date',
      cell: ({ row }) =>
        row.original.startDate === null ? EMPTY_VALUE : formatDisplayDate(row.original.startDate),
    },
    {
      accessorKey: 'deadlineDate',
      header: 'Deadline Date',
      cell: ({ row }) =>
        row.original.deadlineDate === null
          ? EMPTY_VALUE
          : formatDisplayDate(row.original.deadlineDate),
    },
    {
      accessorKey: 'instituteCount',
      header: 'Institutes',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.instituteCount)}</span>
      ),
    },
    {
      accessorKey: 'answerCount',
      header: 'Answers',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.answerCount)}</span>
      ),
    },
    createActionsColumn<BookProject>((row) => (
      <RowActionButton
        label="Book List Entry"
        onClick={() => onEnter(row)}
        ariaLabel={`Open the book list sheet for ${row.name}`}
      />
    )),
  ];
}

const NUMERIC_KEYS: readonly string[] = [];

export function BooklistProjectListPage() {
  const navigate = useNavigate();
  const routeSearch = route.useSearch();
  const routeNavigate = route.useNavigate();
  const controller = useRequisitionFilters({
    search: routeSearch,
    numericKeys: NUMERIC_KEYS,
    onApplyFilters: (next) =>
      void routeNavigate({ search: (previous) => ({ ...previous, ...next }), replace: true }),
    onApplySearch: (text) =>
      void routeNavigate({ search: (previous) => ({ ...previous, search: text }), replace: true }),
  });
  const params = controller.params as StudentProjectSearch;

  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(bookProjectsInfiniteQueryOptions(params));

  const columns = getBookProjectColumns(
    params.search,
    (row) =>
      void navigate({
        to: '/product/booklisted-institution/$projectId',
        params: { projectId: row.id },
      }),
  );

  const stats = [
    { key: 'projects', label: 'Projects', value: firstPageMeta?.total },
    { key: 'answers', label: 'Answers', value: firstPageMeta?.totals.answers },
  ];

  return (
    <CatalogueListLayout
      title="Booklisted Institution"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search projects',
      }}
      stats={{
        cards: stats,
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the projects"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No book list project has been opened"
          {...loadMoreProps}
        />
      )}
    </CatalogueListLayout>
  );
}
